import { useState, useEffect } from 'react';
import type { FinalInputParams, FinalInputState } from '../types';

interface Props {
  params: FinalInputParams;
  puzzleState: FinalInputState;
  onSolve: () => void;
  patchState: (updater: (prev: FinalInputState) => FinalInputState) => void;
}

export function FinalPhraseInput({ params, puzzleState, onSolve, patchState }: Props) {
  const { acceptedPhrase, placeholder } = params;
  const { inputValue, solved, wrong } = puzzleState;
  const [localValue, setLocalValue] = useState(inputValue);

  // Sync local value if state is reset
  useEffect(() => { setLocalValue(inputValue); }, [inputValue]);

  // Clear wrong flag after animation
  useEffect(() => {
    if (!wrong) return;
    const id = setTimeout(() => {
      patchState(s => ({ ...s, wrong: false }));
    }, 800);
    return () => clearTimeout(id);
  }, [wrong]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSubmit() {
    if (solved) return;
    const normalized = localValue.trim().toLowerCase();
    patchState(s => ({ ...s, inputValue: localValue }));
    if (normalized === acceptedPhrase) {
      patchState(s => ({ ...s, solved: true, wrong: false }));
      onSolve();
    } else {
      patchState(s => ({ ...s, wrong: true }));
    }
  }

  if (solved) {
    return (
      <div className="puzzle-final puzzle-final--solved">
        <i className="fa-solid fa-door-open puzzle-final__icon" aria-hidden="true" />
        <p className="puzzle-solved-msg">
          The frame dissolves. You said the right thing.
        </p>
      </div>
    );
  }

  return (
    <div className="puzzle-final">
      <p className="puzzle-narrative">
        Type what you have learned. The phrase you assembled is the key.
      </p>
      <div className={`puzzle-final__input-wrap${wrong ? ' puzzle-final__input-wrap--wrong' : ''}`}>
        <input
          className="puzzle-final__input"
          type="text"
          value={localValue}
          placeholder={placeholder}
          aria-label="Final phrase input"
          onChange={e => setLocalValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSubmit()}
        />
      </div>
      {wrong && (
        <p className="puzzle-error-msg">
          <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Not quite — try again.
        </p>
      )}
      <button
        className="escape-btn escape-btn--primary"
        type="button"
        onClick={handleSubmit}
        disabled={localValue.trim().length === 0}
      >
        <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Submit
      </button>
    </div>
  );
}
